import React, {PureComponent} from 'react';
import styled from 'styled-components';

import Input from '../../components/Input';
import Form from '../../components/Form';
import SideModal from '../../components/SideModal';
import Button from '../../components/Button';
import Icon from '../../components/Icon';

const MAX_FIELD_LENGTH = 128;
const MAX_DB_LENGTH = 255;

class CatalogMetadataForm extends PureComponent {
  state = this.getInitialState();

  getInitialState() {
    const {item} = this.props;
    const existing = item && Array.isArray(item.catalogMeta) ? item.catalogMeta : [];
    return {
      saving: false,
      metadata: existing.map(entry => ({key: entry.key, value: entry.value}))
    };
  }

  componentDidUpdate(prevProps) {
    const {item, open} = this.props;
    const opened = open && !prevProps.open;

    if (prevProps.item !== item || opened) {
      this.setState(this.getInitialState());
    }
  }

  addRow = () => {
    this.setState(state => ({
      metadata: [...state.metadata, {key: '', value: ''}]
    }));
  }

  removeRow = index => {
    this.setState(state => ({
      metadata: state.metadata.filter((_, i) => i !== index)
    }));
  }

  updateRow = (index, fields) => {
    this.setState(state => ({
      metadata: state.metadata.map((row, i) => i === index ? {...row, ...fields} : row)
    }));
  }

  updateKey = (index, value) => {
    const tooLong = value.length > MAX_FIELD_LENGTH;
    const duplicate = this.state.metadata.some(row => row.key === value);
    const keyErrorString = duplicate ? 'Key must be unique' :
        tooLong ? `Key cannot be longer than ${MAX_FIELD_LENGTH} characters` : '';
    this.updateRow(index, {
      key: tooLong ? value.slice(0, MAX_FIELD_LENGTH) : value,
      keyError: duplicate || tooLong,
      keyErrorString: keyErrorString
    });
  }

  updateValue = (index, value) => {
    const tooLong = value.length > MAX_DB_LENGTH;
    this.updateRow(index, {
      value: tooLong ? value.slice(0, MAX_DB_LENGTH) : value,
      valueError: tooLong
    });
  }

  handleSave = async () => {
    const {item, onSave} = this.props;
    const catalogMeta = this.state.metadata
      .map(row => ({key: row.key.trim(), value: row.value.trim()}))
      .filter(row => row.key !== '' && row.value !== '');

    this.setState({saving: true});

    if (!await onSave({...item, catalogMeta})) {
      this.setState({saving: false});
    }
  }

  render() {
    const {metadata, saving} = this.state;
    const {open, onClose} = this.props;
    const hasKeyErrors = metadata.some(row => row.keyError);
    const hasValueErrors = metadata.some(row => row.valueError);
    const hasMissingFieldErrors = metadata.length > 0 && metadata.some(row => row.key === '' || row.value === '');

    return (
      <Form onSubmit={this.handleSave}>
        <SideModal
          title={"Item Attributes"}
          open={open}
          onClose={onClose}
          options={[
            <Button type="submit" disabled={saving || hasKeyErrors || hasValueErrors || hasMissingFieldErrors}>Save Attributes</Button>,
            <Button secondary onClick={onClose}>Cancel</Button>
          ]}>
          <SideModal.Content>
            {metadata.length < 1 ? (
              <EmptyMessage>No attributes yet. Add a key/value pair below.</EmptyMessage>
            ) : metadata.map((row, index) => (
              <MetadataRow key={index}>
                <KeyField>
                  <Input
                    type="text"
                    id={`metadata-key-${index}`}
                    label={index === 0 ? 'Key' : undefined}
                    value={row.key}
                    invalid={row.keyError}
                    validationText={row.keyErrorString}
                    onChange={e => this.updateKey(index, e.target.value)}
                  />
                </KeyField>
                <ValueField>
                  <Input
                    type="text"
                    id={`metadata-value-${index}`}
                    label={index === 0 ? 'Value' : undefined}
                    value={row.value}
                    invalid={row.valueError}
                    validationText={`Value cannot be longer than ${MAX_DB_LENGTH} characters`}
                    onChange={e => this.updateValue(index, e.target.value)}
                  />
                </ValueField>
                <RemoveField firstRow={index === 0}>
                  <Button blank onClick={() => this.removeRow(index)}>
                    <Icon name="trash-2" />
                  </Button>
                </RemoveField>
              </MetadataRow>
            ))}
            <AddRowAction>
              <Button secondary onClick={this.addRow}>Add Attribute</Button>
            </AddRowAction>
          </SideModal.Content>
        </SideModal>
      </Form>
    );
  }
}

const MetadataRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-top: 1rem;

  &:first-child {
    margin-top: 0;
  }
`;

const KeyField = styled.div`
  flex: 0 0 10rem;
`;

const ValueField = styled.div`
  flex: 1;
`;

const RemoveField = styled.div`
  flex: 0 0 auto;
  height: calc(2.25rem + 2px);
  display: flex;
  align-items: center;
  margin-top: ${props => props.firstRow ? '2rem' : '0'};
`;

const AddRowAction = styled.div`
  margin-top: 1.5rem;
`;

const EmptyMessage = styled.p`
  margin: 0 0 1rem 0;
  color: #747474;
`;

export default CatalogMetadataForm;